import { BaseFirestorePlugin } from '../core/BaseFirestorePlugin';
import { IFirestoreQueryData } from '../core/types';
import PerformanceMonitoringService from '../../../../services/PerformanceMonitoringService';

interface ApiLoggerConfig {
  enabled: boolean;
  slowQueryThresholdMs: number;
  environment?: string;
  debugSessionId?: string;
}

export class FirestoreSlowQueryApiLoggerPlugin extends BaseFirestorePlugin {
  private config: ApiLoggerConfig;
  private readonly defaultConfig: ApiLoggerConfig = {
    enabled: false,
    slowQueryThresholdMs: 1500, // Default 1.5 second threshold
    environment: process.env.NODE_ENV,
    debugSessionId: undefined
  };

  constructor(config: Partial<ApiLoggerConfig> = {}) {
    super('firestore-slow-query-api-logger', 'Firestore Slow Query API Logger');
    this.config = { ...this.defaultConfig, ...config };
  }

  afterQuery(queryData: IFirestoreQueryData): void {
    if (!this.config.enabled || !queryData.duration) return;

    // Only log slow queries or writes
    if (queryData.queryType !== 'write' && queryData.duration < this.config.slowQueryThresholdMs) return;

    const logData = {
      ...queryData,
      environment: this.config.environment,
      debugSessionId: this.config.debugSessionId,
      timestamp: new Date().toISOString()
    };

    this.logToApi(logData);
  }

  private async logToApi(data: any): Promise<void> {
    try {
      // TODO: Implement actual API call
      // Example structure:
      PerformanceMonitoringService.logFirestorePerformance({
        type: 'query',
        collection: data.collection,
        sdkVersion: data.queryDetails?.metadata?.firestoreVersion,
        queryType: data.queryType,
        duration: data.duration,
        resultCount: data.resultCount,
        filterDetails: data.queryDetails?.filterDetails,
        filterFields: Array.isArray(data.queryDetails?.filterFields) ? data.queryDetails?.filterFields?.map(field => field) : [],
        filterFieldsJoined: Array.isArray(data.queryDetails?.filterFields) ? data.queryDetails?.filterFields?.join(', ') : '',
        orderBy: data.queryDetails?.orderBy,
        limit: data.queryDetails?.limit,
        timestamp: data.timestamp,
        environment: data.environment,
        debugSessionId: data.debugSessionId
      });
    } catch (error) {
      console.warn('[Firestore API Logger] Failed to log to API:', error);
    }
  }

  setDebugSessionId(sessionId: string): void {
    this.config.debugSessionId = sessionId;
  }

  setSlowQueryThreshold(thresholdMs: number): void {
    this.config.slowQueryThresholdMs = thresholdMs;
  }
}
